import { alpha, useTheme } from '@mui/material';

// One look for every DataGrid in the app (first used on the Users page):
// hairline rows, small uppercase header, gold hover, same surface as the card around it.
const useGridStyles = () => {
  const theme = useTheme();

  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.secondary.main;
  const muted = theme.palette.text.secondary;
  const surface = theme.palette.background.alt;
  const line = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const scrollThumb = isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.22)';

  const gridSx = {
    // Grid
    border: 'none',
    color: theme.palette.text.primary,
    '--DataGrid-rowBorderColor': line,
    '--DataGrid-containerBackground': surface,

    '& .MuiDataGrid-root': {
      border: 'none',
    },

    // Cells: one hairline between rows
    '& .MuiDataGrid-cell': {
      borderBottom: `1px solid ${line}`,
      borderTop: 'none',
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },

    // Header: small, uppercase, muted
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: `${surface} !important`,
      borderBottom: `1px solid ${line}`,
    },
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: `${isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)'} !important`,
      color: muted,
      borderBottom: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    },
    '& .MuiDataGrid-columnSeparator': {
      color: line,
    },
    '& .MuiDataGrid-iconButtonContainer .MuiSvgIcon-root, & .MuiDataGrid-menuIcon .MuiSvgIcon-root': {
      color: muted,
    },

    // Header scrollbar/filler
    '& .MuiDataGrid-scrollbarFiller': {
      backgroundColor: `${surface} !important`,
    },
    '& .MuiDataGrid-scrollbarFiller--header': {
      backgroundColor: `${surface} !important`,
    },

    // Scrollbars: the grid hides the body's native scrollbars and draws its own scrollbar elements
    // (.MuiDataGrid-scrollbar--vertical / --horizontal). Style only those: transparent track on the
    // card surface, thin rounded handle, accent while hovered/dragged.
    // Firefox only: in Chrome/Edge 121+ setting scrollbar-width / scrollbar-color switches off the
    // ::-webkit-scrollbar rules below (no hover color, no rounded handle)
    '@supports not selector(::-webkit-scrollbar)': {
      '& .MuiDataGrid-scrollbar': {
        scrollbarWidth: 'thin',
        scrollbarColor: `${scrollThumb} transparent`,
      },
      '& .MuiDataGrid-scrollbar--vertical + div[aria-hidden="true"]': {
        scrollbarWidth: 'none',
      },
    },
    '& .MuiDataGrid-scrollbar': {
      backgroundColor: 'transparent',
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar': {
      width: 10,
      height: 10,
      backgroundColor: 'transparent',
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar-thumb': {
      backgroundColor: scrollThumb,
      borderRadius: 10,
      border: '3px solid transparent',     // inset look: the visible handle is 4px thick
      backgroundClip: 'content-box',
    },
    // Keep the handle easy to grab in both directions
    '& .MuiDataGrid-scrollbar--vertical::-webkit-scrollbar-thumb': {
      minHeight: 40,
    },
    '& .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar-thumb': {
      minWidth: 40,
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar-thumb:hover': {
      backgroundColor: alpha(accent, 0.75),
      border: '2px solid transparent',     // a little wider while hovered
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar-thumb:active': {
      backgroundColor: accent,
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar-corner': {
      backgroundColor: 'transparent',
    },
    // When the grid scrolls both ways it adds a small corner block after the vertical scrollbar.
    // It has no class name and is "overflow: scroll", so it always paints a pair of native
    // scrollbars: the white square at the bottom right. Show it as plain card surface instead.
    '& .MuiDataGrid-scrollbar--vertical + div[aria-hidden="true"]': {
      backgroundColor: surface,
    },
    '& .MuiDataGrid-scrollbar--vertical + div[aria-hidden="true"]::-webkit-scrollbar, & .MuiDataGrid-scrollbar--vertical + div[aria-hidden="true"]::-webkit-scrollbar-corner': {
      backgroundColor: 'transparent',
    },
    '& .MuiDataGrid-scrollbar--vertical + div[aria-hidden="true"]::-webkit-scrollbar-thumb, & .MuiDataGrid-scrollbar--vertical + div[aria-hidden="true"]::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },

    // Rows / body (the filler is the strip under the last row, behind the horizontal scrollbar)
    '& .MuiDataGrid-virtualScroller, & .MuiDataGrid-filler': {
      backgroundColor: surface,
    },
    '& .MuiDataGrid-row': {
      transition: 'background-color 0.15s ease',
    },
    '& .MuiDataGrid-row:hover': {
      backgroundColor: alpha(accent, isDark ? 0.06 : 0.08),
    },

    // Footer
    '& .MuiDataGrid-footerContainer': {
      backgroundColor: surface,
      color: muted,
      borderTop: `1px solid ${line}`,
      alignItems: 'center',          // keep all footer items vertically centered
    },

    // Pagination
    // Pagination scrolls sideways (MUI sets overflow: auto) when the grid is narrower than
    // "Rows per page … 1–5 of 5 ‹ ›": give that scrollbar the same look as the grid's own
    '& .MuiTablePagination-root': {
      marginBottom: 0,
      '&::-webkit-scrollbar': { height: 8, backgroundColor: 'transparent' },
      '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: scrollThumb,
        borderRadius: 8,
        border: '2px solid transparent',
        backgroundClip: 'content-box',
      },
      '&::-webkit-scrollbar-thumb:hover': { backgroundColor: alpha(accent, 0.75) },
      '&::-webkit-scrollbar-button': { display: 'none' },
      '@supports not selector(::-webkit-scrollbar)': {
        scrollbarWidth: 'thin',
        scrollbarColor: `${scrollThumb} transparent`,
      },
    },
    '& .MuiTablePagination-toolbar': {
      minHeight: '52px',
      alignItems: 'center',
      padding: 0,
    },
    '& .MuiTablePagination-selectLabel': {          // "Rows per page:" label
      marginBottom: 0,
    },
    '& .MuiTablePagination-select': {               // rows-per-page select
      marginBottom: 0,
    },
    '& .MuiTablePagination-displayedRows': {
      marginTop: 0,
      marginBottom: 0,
      lineHeight: 1,
      alignSelf: 'center',
    },

    // Toolbar text
    '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
      color: `${theme.palette.secondary[200]} !important`,
    },
  };

  // Thin outline for the card that holds the grid
  const gridBorder = `1px solid ${line}`;

  // Selected row (e.g. Region): gold tint + gold bar on the left
  const selectedRowSx = {
    backgroundColor: `${alpha(accent, isDark ? 0.12 : 0.14)} !important`,
    boxShadow: `inset 3px 0 0 ${accent}`,
  };

  return { gridSx, gridBorder, selectedRowSx };
};

export default useGridStyles;
